/**
 * The shortcut recorder's session state (plan D3: the recorder runs inside the
 * dispatcher). While a session is active the dispatcher hands every usable
 * keydown here — cancelled, so no command, Monaco, dialog or native menu sees
 * it — instead of matching commands. `installKeybindingDispatcher.ts`
 * re-exports the public functions, so callers keep importing them from there.
 */
import { eventToChord, type KeyboardEventLike } from '../model/chord';
import type { KeyboardLayoutSnapshot } from '../model/keybindingTypes';
import { keyboardLayoutMap } from '../store/keyboardLayout';

export type KeybindingRecorder = (event: KeyboardEvent) => void;

const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'OS']);

let recorder: KeybindingRecorder | null = null;
let recording = false;
let recordingSession = 0;
let cancelActiveRecording: (() => void) | null = null;
/** The physical key whose keydown ended the last session, until it is released. */
let heldKeyCode: string | null = null;

export function setRecorder(nextRecorder: KeybindingRecorder | null): void {
  recorder = nextRecorder;
}

/**
 * `onCancel` hears when the dispatcher ends the session itself (window blur,
 * or a newer recording), so the recorder UI never outlives capture. The
 * returned stop ends this session only, never a newer one.
 */
export function startRecording(nextRecorder?: KeybindingRecorder, onCancel?: () => void): () => void {
  const displacedCancel = recording ? cancelActiveRecording : null;
  if (nextRecorder) recorder = nextRecorder;
  recording = true;
  cancelActiveRecording = onCancel ?? null;
  const session = ++recordingSession;
  if (displacedCancel && displacedCancel !== onCancel) displacedCancel();
  return () => {
    if (session === recordingSession) stopRecording();
  };
}

export function stopRecording(): void {
  recording = false;
  cancelActiveRecording = null;
}

/** The dispatcher's window-blur handler: end the session and tell the recorder. */
export function cancelRecording(): void {
  const cancel = recording ? cancelActiveRecording : null;
  heldKeyCode = null;
  stopRecording();
  cancel?.();
}

/** The dispatcher's keyup handler: the key that ended a session was released. */
export function releaseRecorderHeldKey(event: KeyboardEvent): void {
  if (event.code === heldKeyCode) heldKeyCode = null;
}

/**
 * Keys the recorder can make something of. A lone modifier passes through.
 * Dead keys, composition and AltGr (D5: they produce characters, never chords)
 * are handed over too, so the recorder can say why it refuses them.
 */
function recorderAccepts(event: KeyboardEvent, layout: KeyboardLayoutSnapshot): boolean {
  if (MODIFIER_KEYS.has(event.key)) return false;
  if (event.key === 'Dead' || event.isComposing || event.getModifierState('AltGraph')) return true;
  const probe: KeyboardEventLike = {
    key: event.key,
    code: event.code,
    ctrlKey: true,
    metaKey: false,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
    location: event.location,
    isComposing: event.isComposing,
    getModifierState: (key) => event.getModifierState(key),
  };
  return eventToChord(probe, layout) !== null;
}

function consume(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Returns true when the recorder owns this keydown, so the dispatcher matches
 * no command for it:
 *  - while recording, every keydown (usable keys are cancelled and handed over);
 *  - after a session ended on a keydown, that key's auto-repeat until it is
 *    released — holding ⌘W while recording Close Tab must not close tabs, and
 *    holding Esc must not go on to close the Settings dialog.
 */
export function routeKeydownToRecorder(event: KeyboardEvent, layout: KeyboardLayoutSnapshot): boolean {
  if (heldKeyCode !== null && event.code === heldKeyCode) {
    if (event.repeat) {
      consume(event);
      return true;
    }
    heldKeyCode = null;
  }
  if (!recording) return false;
  // Capture below stops the layout cache's own (bubbling) keydown listener,
  // so let it see the key first: a layout switch is still detected mid-recording.
  keyboardLayoutMap.observeKeydown(event);
  if (recorderAccepts(event, layout)) {
    consume(event);
    recorder?.(event);
    if (!recording) heldKeyCode = event.code;
  }
  return true;
}
